"use client";

import useFormSubmit from "@/hooks/useApiSubmit";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Field,
  Fieldset,
  Form,
  PhoneField,
  TextArea,
  Typography,
} from "@repo/ui";
import { useRouter } from "next/navigation";
import { useMemo } from "react";
import * as Yup from "yup";
import { useAppDispatch } from "@/store/hooks";
import { setNameAndMessage } from "@/store/features/appSlice";
import clsx from "clsx";
import TermsCheckbox from "./TermsCheckbox";
import { useCountryCode } from "@/hooks/useCountryCode";

const trainingProgramSchema = Yup.object().shape({
  // programName: Yup.string().required("Program name is required"),
  // languages: Yup.string().required("Languages are required"),

  languageOnlinePrice: Yup.number()
    .typeError("Must be a number")
    .required("Language online price is required"),

  localLanguageOnlinePrice: Yup.number()
    .typeError("Must be a number")
    .required("Local language online price is required"),

  languageClassroomPrice: Yup.number()
    .typeError("Must be a number")
    .required("Language classroom price is required"),

  localLanguageClassroomPrice: Yup.number()
    .typeError("Must be a number")
    .required("Local language classroom price is required"),

  // batchSize: Yup.number()
  //   .typeError("Must be a number")
  //   .integer("Batch size must be an integer")
  //   .required("Batch size is required"),
});

const QueryFormSchema = Yup.object().shape({
  fullName: Yup.string().required().min(2, "Too Short!").required("Required"),
  contactNumber: Yup.string()
    .required()
    .min(10, "Too Short!")
    .max(16, "Too Long!")
    .required("Required"),
  email: Yup.string().email().required(),
  trainingPrograms: Yup.array().of(trainingProgramSchema),
});

const program = {
  programName: "",
  languages: "",
  languageOnlinePrice: "",
  localLanguageOnlinePrice: "",
  languageClassroomPrice: "",
  localLanguageClassroomPrice: "",
  batchSize: "",
};

const DEFAULT_FORM_VALUES = {
  fullName: "",
  email: "",
  contactNumber: "",
  countryName: "",
  cityName: "",
  linkedIn: "",
  filePath: "",
  certificatePath: "",
  experience: "",
  message: "",
  enquiryType: "GLOBAL_TRAINER_REGISTRATION",
  terms: "false",
  trainingPrograms: [program],
};

function GlobalTrainerRegistrationForm() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { country } = useCountryCode();

  const { handleSubmit } = useFormSubmit({
    onSuccess: (data) => {
      dispatch(setNameAndMessage(data.response));
      router.push("/thank-you-for-enquiry");
    },
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  const initialValues = useMemo(
    () => ({
      ...DEFAULT_FORM_VALUES,
    }),
    []
  );

  const resolveArrayError = (field?: string) => {
    if (typeof field === "string") {
      return field;
    } else {
      return undefined;
    }
  };

  const formContent = useMemo(
    () => (
      <Form
        initialValues={initialValues}
        validationSchema={QueryFormSchema}
        onSubmit={handleSubmit}
        encType="multipart/form-data"
      >
        {({ values, errors, handleChange, setFieldValue, isSubmitting }) => (
          <>
            <Fieldset columns={2}>
              <Field
                required
                name="fullName"
                label="Full Name"
                value={values.fullName}
                onChange={handleChange}
                placeholder=" "
              />
              <Field
                required
                label="Email ID"
                name="email"
                value={values.email}
                onChange={handleChange}
                placeholder=" "
              />
              <PhoneField
                label="Mobile Number"
                name="contactNumber"
                required
                value={values.contactNumber}
                placeholder=" "
                onChange={(phone: string) =>
                  setFieldValue("contactNumber", phone)
                }
                defaultCountry={country}
              />

              <Field
                label="Country of Residence"
                name="countryName"
                value={values.countryName}
                onChange={handleChange}
                placeholder=" "
              />
              <Field
                label="City"
                name="cityName"
                value={values.cityName}
                onChange={handleChange}
                placeholder=" "
              />

              <Field
                label="LinkedIn Profile URL"
                type="url"
                name="linkedIn"
                value={values.linkedIn}
                onChange={handleChange}
                placeholder=" "
              />

              {/* Training progprams addable group  */}

              <div className="sm:col-span-2">
                {/* <div>Training programs</div> */}
                {values.trainingPrograms.map((item, index) => (
                  <Fieldset
                    key={index}
                    columns={2}
                    className={clsx(
                      "border-t border-dashed border-gray-300 pt-4",
                      {
                        "border-b-0": index === 0,
                      }
                    )}
                  >
                    <Field
                      label="Training Program Name"
                      name={`trainingPrograms[${index}].programName`}
                      placeholder=" "
                      value={item.programName}
                      onChange={handleChange}
                    />
                    <Field
                      label="Languages You Can Deliver This Training In"
                      name={`trainingPrograms[${index}].languages`}
                      value={item.languages}
                      placeholder="E.g., English, Spanish"
                      onChange={handleChange}
                    />
                    <div className="sm:col-span-2">
                      <Fieldset
                        render={<div />}
                        key={index}
                        columns={2}
                        label="Pricing Per Language & Mode (8-Hour Day Rate in USD)"
                      >
                        <Field
                          label=" "
                          name={`trainingPrograms[${index}].languageOnlinePrice`}
                          value={item.languageOnlinePrice}
                          placeholder="English - Online LVC"
                          onChange={handleChange}
                          error={
                            typeof errors.trainingPrograms?.[index] === "object"
                              ? resolveArrayError(
                                  errors.trainingPrograms[index]
                                    ?.languageOnlinePrice
                                )
                              : undefined
                          }
                        />

                        <Field
                          label=" "
                          name={`trainingPrograms[${index}].languageClassroomPrice`}
                          value={item.languageClassroomPrice}
                          placeholder="English - Classroom"
                          onChange={handleChange}
                          error={
                            typeof errors.trainingPrograms?.[index] === "object"
                              ? resolveArrayError(
                                  errors.trainingPrograms[index]
                                    ?.languageClassroomPrice
                                )
                              : undefined
                          }
                        />
                        <Field
                          label=" "
                          name={`trainingPrograms[${index}].localLanguageOnlinePrice`}
                          value={item.localLanguageOnlinePrice}
                          placeholder="Local Lang - Online LVC"
                          onChange={handleChange}
                          helpText="Fill only the languages/modes applicable to you for this course. Example: 350 USD/day"
                          error={
                            typeof errors.trainingPrograms?.[index] === "object"
                              ? resolveArrayError(
                                  errors.trainingPrograms[index]
                                    ?.localLanguageOnlinePrice
                                )
                              : undefined
                          }
                        />
                        <Field
                          label=" "
                          name={`trainingPrograms[${index}].localLanguageClassroomPrice`}
                          value={item.localLanguageClassroomPrice}
                          placeholder="Local Lang - Classroom"
                          onChange={handleChange}
                          error={
                            typeof errors.trainingPrograms?.[index] === "object"
                              ? resolveArrayError(
                                  errors.trainingPrograms[index]
                                    ?.localLanguageClassroomPrice
                                )
                              : undefined
                          }
                        />
                      </Fieldset>
                    </div>
                    <Field
                      label="Maximum Batch Size"
                      type="number"
                      name={`trainingPrograms[${index}].batchSize`}
                      placeholder=" "
                      value={item.batchSize}
                      onChange={handleChange}
                      colSpan={2}
                      error={
                        typeof errors.trainingPrograms?.[index] === "object"
                          ? resolveArrayError(
                              errors.trainingPrograms[index]?.batchSize
                            )
                          : undefined
                      }
                    />

                    <div
                      className={clsx("mb-5", {
                        "!hidden": values.trainingPrograms.length === 1,
                      })}
                    >
                      <Button
                        color="error"
                        size="small"
                        type="button"
                        onClick={() => {
                          const updatedTrainingPrograms = [
                            ...values.trainingPrograms,
                          ];
                          updatedTrainingPrograms.splice(index, 1);
                          setFieldValue(
                            "trainingPrograms",
                            updatedTrainingPrograms
                          );
                        }}
                      >
                        Remove
                      </Button>
                    </div>
                  </Fieldset>
                ))}
                <div className="col-span-2 flex justify-center">
                  <Button
                    color="primary"
                    size="small"
                    type="button"
                    onClick={() =>
                      setFieldValue("trainingPrograms", [
                        ...values.trainingPrograms,
                        program,
                      ])
                    }
                  >
                    + Add Another Training Program
                  </Button>
                </div>
              </div>

              <Field
                label="Upload Resume"
                name="filePath"
                type="file"
                helpText="PDF format only, max 10MB"
                inputProps={{
                  accept: "application/pdf",
                }}
                onChange={(event) => {
                  const target = event.target as unknown as HTMLInputElement;
                  const file = target.files?.[0];
                  if (file) {
                    setFieldValue("filePath", file);
                  }
                }}
              />
              {/* Certificate upload field */}
              <Field
                label="Upload Certifications"
                name="certificatePath"
                type="file"
                helpText="Upload relevant training certifications (PDF/ZIP)"
                inputProps={{
                  accept: ".pdf,application/pdf,.zip,application/zip",
                }}
                onChange={(event) => {
                  const target = event.target as unknown as HTMLInputElement;
                  const file = target.files?.[0];
                  if (file) {
                    setFieldValue("certificatePath", file);
                  }
                }}
              />

              <TextArea
                name="experience"
                label="Brief About Your Training Experience"
                colSpan={2}
                value={values.experience}
                placeholder="Mention your areas of expertise, years of experience, industries served, etc."
                onChange={handleChange}
              />
              <TextArea
                name="message"
                label="Any Other Notes/Preferences"
                colSpan={2}
                value={values.message}
                onChange={handleChange}
              />
            </Fieldset>

            <TermsCheckbox
              onCheckedChange={(value) => setFieldValue("terms", value)}
              value={values.terms}
            />
            <div className="text-right mt-4">
              <Button type="submit" color="primary" loading={isSubmitting}>
                Submit Registration
              </Button>
            </div>
            {/* <pre>{JSON.stringify(values, null, 2)}</pre>
            <pre>{JSON.stringify(errors, null, 2)}</pre> */}
          </>
        )}
      </Form>
    ),
    [country, handleSubmit, initialValues]
  );

  return (
    <Card className="border">
      <CardHeader className="!p-8 !pb-6">
        <Typography variant="h2">Global Trainer Registration</Typography>
      </CardHeader>
      <CardContent className="!p-8 !pt-0">{formContent}</CardContent>
    </Card>
  );
}

export default GlobalTrainerRegistrationForm;
